import { Box, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';

type AssessmentEvidenceSummaryProps = {
  completed: number;
  enrolled: number;
  participating: number;
  ready: number;
  readyWithReview: number;
  notReady: number;
  incomplete: number;
};

function MetricRing({
  label,
  tooltip,
  value,
  color,
}: {
  label: string;
  tooltip: string;
  value: number;
  color: string;
}) {
  const normalizedValue = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;

  return (
    <Tooltip title={tooltip}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={40}
            thickness={4.5}
            sx={{ color: 'action.disabledBackground' }}
          />
          <CircularProgress
            variant="determinate"
            value={normalizedValue}
            size={40}
            thickness={4.5}
            sx={{ color, position: 'absolute', left: 0 }}
          />
          <Box
            sx={{
              inset: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" fontWeight={700}>
              {Math.round(normalizedValue)}%
            </Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

export default function AssessmentEvidenceSummary({
  completed,
  enrolled,
  ready,
  readyWithReview,
  notReady,
}: AssessmentEvidenceSummaryProps) {
  const completedDenominator = completed > 0 ? completed : 0;

  const readyPct = completedDenominator > 0 ? (ready / completedDenominator) * 100 : 0;
  const readyWithReviewPct = completedDenominator > 0 ? (readyWithReview / completedDenominator) * 100 : 0;
  const notReadyPct = completedDenominator > 0 ? (notReady / completedDenominator) * 100 : 0;
  

  return (
    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
      <Tooltip title="Completed / Enrolled">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <PeopleOutlineIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600}>
            {completed}/{enrolled}
          </Typography>
        </Stack>
      </Tooltip>
      <MetricRing
        label="Not Ready"
        tooltip="Limited Evidence of Readiness"
        value={notReadyPct}
        color="#d32f2f"
      />
      <MetricRing
        label="Ready w/ Review"
        tooltip="Partial Evidence of Readiness"
        value={readyWithReviewPct}
        color="#ed6c02"
      />
      <MetricRing
        label="Ready"
        tooltip="Strong Evidence of Readiness"
        value={readyPct}
        color="#2e7d32"
      />
    </Stack>
  );
}
